import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { AuthState, AuthUser, MeResponse } from '../types/auth';
import { config, loadServerConfig } from '../config/env';
import { ensureFreshSession } from '../utils/tokenValidation';
import { setSessionExpiry } from '../utils/session';
import { initiateLogin, shouldAutoLogin } from '../utils/oauth';
import { ErrorDisplay } from '../components/ErrorDisplay.tsx';
import { LoadingDisplay } from '../components/LoadingDisplay.tsx';
import { BootShell } from '../components/BootShell.tsx';

interface AuthContextType {
  user: AuthUser | null;
  ensureFreshSession: (force?: boolean) => Promise<boolean>;
  login: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

async function fetchMe(): Promise<MeResponse | null> {
  // Bound the request so a stalled /auth/me can't wedge the viewer on loading.
  const response = await fetch(`${config.apiBaseUrl}/auth/me`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    signal: AbortSignal.timeout(10000),
  });
  if (!response.ok) return null;
  return (await response.json()) as MeResponse;
}

function AutoLogin({ onLogin }: { onLogin: () => Promise<void> }) {
  useEffect(() => {
    onLogin().catch(console.error);
  }, [onLogin]);

  return (
    <BootShell>
      <LoadingDisplay message="Redirecting…" subtitle="Logging in…" />
    </BootShell>
  );
}

function SignedOut({ onLogin }: { onLogin: () => Promise<void> }) {
  return (
    <div className="flex items-center justify-center h-full bg-ground">
      <div className="text-center p-6 max-w-md mx-auto">
        <h2 className="text-sm font-medium text-ink mb-1">
          You are signed out
        </h2>
        <p className="text-xs text-muted mb-4">
          Your Hawk session has ended. Your identity provider session may still
          be active, so logging back in may not prompt for credentials.
        </p>
        <button
          onClick={() => {
            onLogin().catch(console.error);
          }}
          className="px-4 py-2 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors"
        >
          Log in
        </button>
      </div>
    </div>
  );
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null,
  });

  const ensureFreshSessionCallback = useCallback(
    (force?: boolean): Promise<boolean> => ensureFreshSession(force),
    []
  );

  const loginCallback = useCallback(async (): Promise<void> => {
    await initiateLogin();
  }, []);

  const loginFromSignedOut = useCallback(async (): Promise<void> => {
    await initiateLogin('/');
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initializeAuth() {
      try {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        await loadServerConfig();

        // Identity comes from the cookie-authenticated /auth/me. If it's not
        // authenticated yet, try one refresh (a valid refresh cookie may still
        // be present after a page load) and retry once before giving up.
        let me = await fetchMe();
        if (!me) {
          const refreshed = await ensureFreshSession();
          if (refreshed) me = await fetchMe();
        }
        if (cancelled) return;

        if (me) {
          if (me.exp != null) setSessionExpiry(me.exp);
          setAuthState({
            user: {
              sub: me.sub,
              email: me.email ?? undefined,
              isAdmin: me.is_admin ?? false,
              modelGroups: me.model_groups ?? [],
              codeGroups: me.code_groups ?? [],
            },
            isLoading: false,
            error: null,
          });
        } else {
          // Not authenticated — surface no error, just prompt login.
          setAuthState({ user: null, isLoading: false, error: null });
        }
      } catch (error) {
        if (cancelled) return;
        setAuthState({
          user: null,
          isLoading: false,
          error: `Authentication failed: ${error instanceof Error ? error.message : String(error)}`,
        });
      }
    }

    void initializeAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      user: authState.user,
      ensureFreshSession: ensureFreshSessionCallback,
      login: loginCallback,
    }),
    [authState.user, ensureFreshSessionCallback, loginCallback]
  );

  if (authState.isLoading) {
    return (
      <BootShell>
        <LoadingDisplay message="Loading…" subtitle="Authenticating…" />
      </BootShell>
    );
  }

  if (authState.error) {
    return (
      <ErrorDisplay message={`Authentication Error: ${authState.error}`} />
    );
  }

  if (!authState.user) {
    return shouldAutoLogin(window.location.search) ? (
      <AutoLogin onLogin={loginCallback} />
    ) : (
      <BootShell>
        <SignedOut onLogin={loginFromSignedOut} />
      </BootShell>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
